/**
 * Tiny fetch wrapper for the Sentinel Console.
 *
 *   - All requests prefix /api/v1
 *   - All requests send credentials (session cookie)
 *   - On any state-mutating method (POST/PATCH/PUT/DELETE), the wrapper
 *     fetches a CSRF token from /api/v1/csrf-token (cached for 5 minutes
 *     per session) and forwards it as `x-csrf-token`
 *   - Throws ApiError on non-2xx with the parsed JSON body attached
 *
 * Lesson 45: clearCsrfToken() must be called on login AND logout because
 * the csrf-csrf v4 token is bound to the session id.
 */

const BASE = "/api/v1";
const CSRF_TTL_MS = 5 * 60 * 1000;

export class ApiError extends Error {
  status: number;
  detail?: unknown;

  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

interface CsrfCache {
  token: string;
  fetchedAt: number;
}

let csrfCache: CsrfCache | null = null;

export function clearCsrfToken(): void {
  csrfCache = null;
}

async function fetchCsrfToken(): Promise<string> {
  if (csrfCache && Date.now() - csrfCache.fetchedAt < CSRF_TTL_MS) {
    return csrfCache.token;
  }
  const res = await fetch(`${BASE}/csrf-token`, { credentials: "include" });
  if (!res.ok) {
    throw new ApiError(res.status, `failed to fetch csrf token: ${res.status}`);
  }
  const json = (await res.json()) as { csrfToken: string };
  csrfCache = { token: json.csrfToken, fetchedAt: Date.now() };
  return json.csrfToken;
}

const MUTATING_METHODS = new Set(["POST", "PATCH", "PUT", "DELETE"]);

export async function api<T>(
  path: string,
  opts: RequestInit = {},
): Promise<T> {
  const method = (opts.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    "content-type": "application/json",
    ...(opts.headers as Record<string, string> | undefined ?? {}),
  };

  // Attach CSRF token to mutating requests. We skip /auth/login because
  // the server exempts it (no session yet to bind to).
  if (MUTATING_METHODS.has(method) && !path.startsWith("/auth/login")) {
    try {
      headers["x-csrf-token"] = await fetchCsrfToken();
    } catch {
      // If the token fetch fails (likely because the session is gone),
      // let the underlying request fall through and surface the 401/403.
    }
  }

  // headers last so callers can't accidentally overwrite `content-type` or
  // `x-csrf-token` by passing a `headers` key via `opts` (which spreads
  // first). Same for `credentials` — force "include" always.
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    credentials: "include",
    headers,
  });

  // If we got a CSRF error, drop the cached token so the next call refetches.
  if (res.status === 403) {
    let body: unknown = null;
    try {
      body = await res.clone().json();
    } catch {
      /* ignore */
    }
    if (
      body &&
      typeof body === "object" &&
      "error" in body &&
      (body as { error: unknown }).error === "invalid_csrf_token"
    ) {
      clearCsrfToken();
    }
  }

  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!res.ok) {
    // Server contract (app.ts): { error: code, message: user-facing, details?, requestId }
    // Prefer `message` for the ApiError string so callers can match on
    // `err.message === "account temporarily locked"` etc. Fall back to
    // `error` (machine code) if the server skipped a message, and finally
    // to the HTTP status line.
    let message = `HTTP ${res.status}`;
    if (body && typeof body === "object") {
      const b = body as { message?: unknown; error?: unknown };
      if (typeof b.message === "string" && b.message.length > 0) {
        message = b.message;
      } else if (typeof b.error === "string" && b.error.length > 0) {
        message = b.error;
      }
    }
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

// ── §5.8 S-05 — Shift KPI bar ────────────────────────────────────────

export interface ShiftKpi {
  site: string;
  since: string;
  eventsTotal: number;
  autoHandled: number;
  operatorInterrupts: number;
  /** null when no incidents reached ACTIVE in the window. */
  falseAlarmRate: number | null;
  /** null when no incidents reached ACTIVE in the window. */
  meanVerifySecondsP50: number | null;
  activeCount: number;
  escalatedCount: number;
  generatedAt: string;
}

export async function fetchKpiShift(
  siteId: string,
  since?: string,
): Promise<ShiftKpi> {
  const qs = new URLSearchParams({ site: siteId });
  if (since) qs.set("since", since);
  return api<ShiftKpi>(`/kpi/shift?${qs.toString()}`);
}

// ── §5.8 S-06 — Waterfall diagram ────────────────────────────────────

export interface WaterfallApiData {
  site: string;
  windowStart: string;
  windowEnd: string;
  bucketSeconds: number;
  segments: string[];
  rows: { t: string; energies: number[] }[];
  generatedAt: string;
}

export async function fetchWaterfall(
  siteId: string,
  opts: { windowSeconds?: number; bucketSeconds?: number } = {},
): Promise<WaterfallApiData> {
  const qs = new URLSearchParams({ site: siteId });
  if (opts.windowSeconds) qs.set("window_seconds", String(opts.windowSeconds));
  if (opts.bucketSeconds) qs.set("bucket_seconds", String(opts.bucketSeconds));
  return api<WaterfallApiData>(`/kpi/waterfall?${qs.toString()}`);
}
